import { FC, Fragment, PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  tableRowClasses,
  Tooltip,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import DOMPurify from 'dompurify';
import { v4 } from 'uuid';

import { FlagChip, ImageWithSkeleton, ListSubheader, TitleTypeChip } from '@components/base';
import { TagGrid } from '@components/content';
import { EpisodeInfo } from '@components/media';
import { SeriesCountDisplay } from '@components/media';
import useImageGroupData from '@hooks/imageHooks/useImageGroupData';
import useGenericDataContext from '@hooks/useGenericDataContext';
import useMetadataContext from '@hooks/useMetadataContext';
import { useProviderNames } from '@hooks/useProviderNames';
import useTitleContext from '@hooks/useTitleContext';
import { Cast, DetailedTitle, TitleType } from '@models/Title';
import { getAvailabilityWindow } from '@utils/availabilityUtils';
import { formatDate } from '@utils/dateUtils';
import { getDurStr } from '@utils/titleUtils';

import HighlightedTypography, { Highlighted } from '../HighlightedTypography';

import PlaybackOptions from './PlaybackOptions';
import ScoreRating from './ScoreRating';
import Section, { SectionHeading } from './Section';

const AgeRating = styled(Typography)`
  align-items: center;
  background: ${({ theme }) => theme.palette.action.disabledBackground};
  display: flex;
  padding: ${({ theme }) => theme.spacing(0, 1)};
`;

type DetailTitle = DetailedTitle & {
  mergeReason?: string;
};

type PropsWithTitle = {
  title: DetailTitle;
};

export const KeyInfo: FC<PropsWithTitle> = ({
  title,
  title: { ageRating, type, duration, productionYear, country, price },
}) => {
  const { t } = useTranslation();
  const { playbackOptionId } = useTitleContext();

  const durationStr = getDurStr(t, duration);

  const info = [
    ...(durationStr ? [durationStr] : []),
    ...(productionYear ? [productionYear] : []),
    ...(price ? [t('price.value', { price })] : []),
  ].join(t('middot'));

  return (
    <Stack spacing={1}>
      <Stack alignItems='center' direction='row' spacing={1}>
        <TitleTypeChip titleType={title.type} />

        {ageRating != null && (
          <Tooltip title={t('ageRating.tooltip', { count: ageRating })}>
            <AgeRating variant='body2'>{t('ageRating', { count: ageRating })}</AgeRating>
          </Tooltip>
        )}

        {country &&
          country
            .split(',')
            .map(country => (
              <FlagChip
                color={playbackOptionId ? 'info' : undefined}
                countryCode={country}
                key={country}
              />
            ))}

        {info && <Typography>{info}</Typography>}
      </Stack>

      {type === 'series' && <SeriesCountDisplay series={title.series} variant='overline' />}

      <EpisodeInfo displaySeriesAsLink title={title} />
    </Stack>
  );
};

const StyledTable = styled(Table)`
  .${tableRowClasses.root} {
    &:hover {
      background: ${({ theme }) => theme.palette.action.hover};
    }

    .${tableCellClasses.root} {
      &:first-of-type {
        vertical-align: baseline;
      }

      &:last-of-type {
        color: ${({ theme }) => theme.palette.text.secondary};
      }
    }

    &:last-of-type .${tableCellClasses.root} {
      border: none;

      ${({ theme }) => theme.breakpoints.up('sm')} {
        white-space: nowrap;
      }
    }
  }
`;

const LoadingValue = () => <Skeleton height={24} variant='rounded' width={120} />;

const ValueCell: FC<PropsWithChildren<{ loading: boolean }>> = ({ children, loading }) => (
  <TableCell>{loading ? <LoadingValue /> : children}</TableCell>
);

const LoadingPosterImages = () => (
  <Grid container spacing={0.5}>
    <Grid size={7}>
      <Skeleton sx={{ aspectRatio: '2 / 3' }} variant='rounded' />
    </Grid>

    <Grid size={5}>
      <Stack spacing={0.5}>
        <Skeleton sx={{ aspectRatio: '16 / 9' }} variant='rounded' />
        <Skeleton sx={{ aspectRatio: '1 / 1' }} variant='rounded' />
      </Stack>
    </Grid>
  </Grid>
);

type InformationProps = {
  loading: boolean;
  title?: DetailTitle;
};

const Information: FC<InformationProps> = ({ loading, title }) => {
  const { t } = useTranslation();
  const { playbackOptionId } = useTitleContext();
  const { getProviderList } = useProviderNames();

  const { flags, type, season, genres, mergeReason, tags, playbackOptions = [] } = title || {};
  const hasFlags = flags && Object.entries(flags).length > 0;
  const hasTags = tags && tags.length > 0;
  const playbackOption = playbackOptionId ? playbackOptions[0] : undefined;
  const { availableFrom, availableTo } = getAvailabilityWindow(playbackOptions);
  const providers = getProviderList(
    Array.from(new Set(playbackOptions.map(({ providerIdentifier }) => providerIdentifier)))
  );
  const metadataRows = [
    {
      label: 'availableFrom',
      value: availableFrom ? formatDate(availableFrom, 'full') : '-',
    },
    {
      label: 'availableTo',
      value: availableTo ? formatDate(availableTo, 'full') : '-',
    },
    ...(playbackOptionId ? [{ label: 'masterId', value: title?.masterId }] : []),
    ...(playbackOptionId
      ? [
          { label: 'providerType', value: playbackOption?.providerType },
          { label: 'platformTitleId', value: playbackOption?.platformTitleId },
          { label: 'providerTitleId', value: playbackOption?.providerTitleId },
        ]
      : []),
    {
      label: playbackOptionId ? 'providerIdentifier' : 'providers',
      value:
        playbackOption?.providerIdentifier ||
        (providers.length > 0 ? <TagGrid maxTags={false} tags={providers} /> : '-'),
    },
    ...(playbackOptionId ? [{ label: 'sourceId', value: playbackOption?.sourceId }] : []),
  ];

  return (
    <Box>
      <SectionHeading>{playbackOptionId ? 'PlaybackOption' : t('aggregated')}</SectionHeading>

      <Paper>
        <StyledTable size='small'>
          <TableHead sx={{ ...visuallyHidden }}>
            <TableRow>
              <TableCell>Field</TableCell>
              <TableCell>Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>originalTitle</TableCell>
              <ValueCell loading={loading}>
                {title && (
                  <HighlightedTypography
                    asset={title}
                    component='span'
                    field='originalTitle'
                    variant='inherit'
                  />
                )}
              </ValueCell>
            </TableRow>

            {playbackOptionId && (
              <TableRow>
                <TableCell>id</TableCell>
                <ValueCell loading={loading}>{playbackOption?.id}</ValueCell>
              </TableRow>
            )}

            {!playbackOptionId && (
              <TableRow>
                <TableCell>masterId</TableCell>
                <ValueCell loading={loading}>{title?.masterId}</ValueCell>
              </TableRow>
            )}

            <TableRow>
              <TableCell>descriptionShort</TableCell>
              <ValueCell loading={loading}>
                {title && (
                  <HighlightedTypography
                    asset={title}
                    component='span'
                    field='descriptionShort'
                    variant='inherit'
                  />
                )}
              </ValueCell>
            </TableRow>

            <TableRow>
              <TableCell>descriptionLong</TableCell>
              <ValueCell loading={loading}>
                {title && (
                  <HighlightedTypography
                    asset={title}
                    component='span'
                    field='descriptionLong'
                    variant='inherit'
                  />
                )}
              </ValueCell>
            </TableRow>

            {metadataRows.map(({ label, value }) => (
              <TableRow key={label}>
                <TableCell>{label}</TableCell>
                <ValueCell loading={loading}>{value || '-'}</ValueCell>
              </TableRow>
            ))}

            {type === 'episode' && (
              <TableRow>
                <TableCell>seasonName</TableCell>
                <ValueCell loading={loading}>{season?.titleName}</ValueCell>
              </TableRow>
            )}

            <TableRow>
              <TableCell>genres</TableCell>
              <ValueCell loading={loading}>
                {genres && genres.length > 0 ? <TagGrid maxTags={false} tags={genres} /> : '-'}
              </ValueCell>
            </TableRow>

            {(playbackOptionId || hasTags) && (
              <TableRow>
                <TableCell>tags</TableCell>
                <ValueCell loading={loading}>
                  {tags && tags.length > 0 ? <TagGrid maxTags={false} tags={tags} /> : '-'}
                </ValueCell>
              </TableRow>
            )}

            {title && Object.hasOwn(title, 'mergeReason') && (
              <TableRow>
                <TableCell>mergeReason</TableCell>
                <ValueCell loading={loading}>{mergeReason || 'Initial'}</ValueCell>
              </TableRow>
            )}

            {hasFlags &&
              Object.entries(flags).map(([key, value]) => (
                <TableRow key={key}>
                  <TableCell>{key}</TableCell>
                  <ValueCell loading={loading}>{value}</ValueCell>
                </TableRow>
              ))}
          </TableBody>
        </StyledTable>
      </Paper>
    </Box>
  );
};

const PosterImages: FC<PropsWithTitle> = ({ title: { titleName } }) => {
  const { imageMap } = useImageGroupData();
  const spacing = 0.5;

  return (
    <Grid container spacing={spacing}>
      <Grid
        sx={{
          width: theme =>
            `calc(calc(${(6.125 / 12) * 100}% - ${theme.spacing(spacing / 2)}) + ${theme.spacing(
              spacing * 0.3
            )})`,
        }}
      >
        <ImageWithSkeleton alt={titleName} ratio='poster' src={imageMap.poster?.getSrc(360)} />
      </Grid>

      <Grid size='grow'>
        <Stack spacing={spacing}>
          <ImageWithSkeleton alt={titleName} ratio='wide' src={imageMap.wide?.getSrc(360)} />
          <ImageWithSkeleton alt={titleName} ratio='square' src={imageMap.square?.getSrc(360)} />
        </Stack>
      </Grid>
    </Grid>
  );
};

type CastWithId = Cast & { id: string };

const CastList: FC<PropsWithTitle> = ({ title: { cast: flatCast, masterId } }) => {
  const { highlights } = useMetadataContext();

  const castListWithId = useMemo<CastWithId[]>(
    () => flatCast.map(castMember => ({ ...castMember, id: v4() })),
    [flatCast]
  );

  const castHighlights =
    highlights[masterId] && highlights[masterId]['cast/Name']
      ? highlights[masterId]['cast/Name']
      : [];

  const replaceWithHighlight = (name?: string) =>
    castHighlights.find(highlight => highlight.split(/<\/?em>/gim).join('') === name) || name;

  const groupedCast = castListWithId
    .sort((a, b) => (a.role && b.role ? a.role.localeCompare(b.role) : 0))
    .reduce(
      (acc: Partial<Record<string, CastWithId[]>>, cur) =>
        cur.role
          ? { ...acc, [cur.role]: cur.role in acc ? [...(acc[cur.role] || []), cur] : [cur] }
          : acc,
      {}
    );

  return (
    <Section category='cast' empty={castListWithId.length === 0}>
      {Object.entries(groupedCast).map(([role, cast], index) => (
        <Fragment key={role}>
          <ListSubheader roundedTop={index === 0} variant='soft'>
            {role.replace(/([A-Z])/g, ' $1')}
          </ListSubheader>

          {cast?.map((castMember, index) => (
            <ListItem dense divider={index < cast.length - 1} key={castMember.id}>
              <ListItemText
                primary={
                  <Highlighted
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(replaceWithHighlight(castMember.name) || ''),
                    }}
                  />
                }
              />
            </ListItem>
          ))}
        </Fragment>
      ))}
    </Section>
  );
};

type DetailsProps = {
  titleType?: TitleType;
};

const Details: FC<DetailsProps> = ({ titleType }) => {
  const { data: title } = useGenericDataContext<DetailTitle>();
  const { playbackOptionId } = useTitleContext();
  const loading = !title;
  const keyInfoHeight = ['episode', 'series'].includes(titleType ?? '') ? 64 : 32;

  return (
    <Box height='fit-content'>
      <Grid container spacing={2}>
        <Grid height={keyInfoHeight} size='grow'>
          {title && <KeyInfo title={title} />}
        </Grid>

        <Grid container display='flex' size={12} spacing={2}>
          <Grid size='grow'>
            <Information loading={loading} title={title} />
          </Grid>

          <Grid width={{ xs: 1, sm: 260, xl: 320 }}>
            {title ? <PosterImages title={title} /> : <LoadingPosterImages />}
          </Grid>
        </Grid>

        {title && (
          <Grid container size={12} spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={4}>
                {!playbackOptionId && <PlaybackOptions title={title} />}
                <ScoreRating />
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <CastList title={title} />
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Details;
